import { taskGroupsQuery } from "@common/api/quoting/task-group"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { motion } from "framer-motion"
import { z } from "zod"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { PageContent } from "@/components/page/page-content"
import { requirePermission } from "@/lib/require-permission"
import { NewGroupButton } from "./-components/new-group-button"
import { TaskGroupList } from "./-components/task-group-list"

const filterSchema = z.object({
  search: z.string().optional().catch(""),
  archived: z.boolean().optional().catch(false),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/quoting/task-groups",
)({
  staticData: {
    breadcrumb: [
      { label: "Quoting - Task groups", href: "/settings/quoting/task-groups" },
    ],
  },
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({ archived: search.archived ?? false }),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(
      queryClient,
      "quote_task_group:read",
      "/settings/workspace/general",
    ),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(taskGroupsQuery({ deleted: deps.archived })),
  component: TaskGroupsPage,
})

function TaskGroupsPage(): React.ReactElement {
  const { search = "", archived = false } = Route.useSearch()

  return (
    <div className="flex h-full">
      <motion.div layoutScroll className="min-w-0 flex-1 overflow-y-auto">
        <FilterBar>
          <Toolbar search={search} archived={archived} />
        </FilterBar>
        <PageContent>
          <TaskGroupList search={search} archived={archived} />
        </PageContent>
      </motion.div>
      <Outlet />
    </div>
  )
}

function Toolbar({
  search,
  archived,
}: {
  search: string
  archived: boolean
}): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const canCreate = useHasPermission("quote_task_group:create")
  const filter = (next: string) =>
    navigate({
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })
  const showArchived = (next: boolean) =>
    navigate({
      search: (previous) => ({ ...previous, archived: next || undefined }),
      replace: true,
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) => showArchived(tab === "archived")}
        />
      }
      actions={
        <>
          <NameFilter search={search} onSearch={filter} />
          {canCreate && <ImportCsvButton />}
          {canCreate && <NewGroupButton />}
        </>
      }
    />
  )
}

function ImportCsvButton(): React.ReactElement {
  return (
    <Button asChild size="sm" variant="outline">
      <Link to="/settings/quoting/task-groups/import" search={true}>
        Import
      </Link>
    </Button>
  )
}
